'use client';

import Icon from '@/components/ui/AppIcon';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface OrderSummaryProps {
  cartItems: CartItem[];
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
  onCheckout: () => void;
}

export default function OrderSummary({
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
}: OrderSummaryProps) {
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.08;
  const total = subtotal + tax;

  return (
    <div className="glass-card rounded-2xl p-6 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
      <h2 className="text-2xl font-black text-foreground mb-6 font-heading flex items-center gap-2">
        <Icon name="ShoppingBagIcon" size={24} className="text-primary" />
        Your Order
      </h2>

      {cartItems.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="ShoppingCartIcon" size={48} className="text-muted mx-auto mb-4" />
          <p className="text-muted-foreground">Your cart is empty</p>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="space-y-4 mb-6">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-4 pb-4 border-b border-border"
              >
                <div className="flex-1">
                  <h4 className="font-bold text-foreground mb-1">{item.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    ${item.price.toFixed(2)} each
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-1 bg-accent rounded-full px-2 py-1">
                    <button
                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-primary hover:text-primary-foreground transition-colors"
                      disabled={item.quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      <Icon name="MinusIcon" size={12} />
                    </button>
                    <span className="text-sm font-bold w-6 text-center">{item.quantity}</span>
                    <button
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                      className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-primary hover:text-primary-foreground transition-colors"
                      aria-label="Increase quantity"
                    >
                      <Icon name="PlusIcon" size={12} />
                    </button>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-error hover:text-error-foreground transition-colors"
                    aria-label={`Remove ${item.name}`}
                  >
                    <Icon name="TrashIcon" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Order Totals */}
          <div className="space-y-3 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-semibold text-foreground">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tax (8%)</span>
              <span className="font-semibold text-foreground">${tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg pt-3 border-t border-border">
              <span className="font-bold text-foreground">Total</span>
              <span className="font-black text-primary text-2xl">${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Checkout Button */}
          <button
            onClick={onCheckout}
            className="w-full bg-primary text-primary-foreground py-4 rounded-full font-bold uppercase tracking-wider hover:bg-primary/90 transition-all hover:scale-105 shadow-lg flex items-center justify-center gap-2"
          >
            <Icon name="CheckCircleIcon" size={20} />
            <span>Proceed to Checkout</span>
          </button>
        </>
      )}
    </div>
  );
}